/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useState, useMemo, useCallback, useEffect } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';
import { useNavigate, generatePath } from 'react-router-dom';

import AccountRow from '#client/components/AccountRow';
import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import ConfirmDeletePayeeModal from '#client/components/ConfirmDeletePayeeModal';
import MenuItem from '#client/components/MenuItem';
import MenuItemConnectedAccount from '#client/components/MenuItemConnectedAccount';
import { MODAL_NAMES } from '#client/constants/modals';
import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import useBankingPath from '#client/hooks/useBankingPath';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';

import { PayeeWrapper } from './Payee.styles';

import useGetPayment from '../hooks/useGetPayment';

function Payee({
  id,
  name,
  bank,
  usedAt,
  isLatest,
  accountNumber,
  status,
  accountType,
  connectedType,
  fullAccountNumber,
  wire,
  handleUpdatePayeeStatus,
  handleDeleteSuccess,
  loadingAccounts,
  type,
  routingNumber,
  fullRoutingNumber,
  isWireSupported,
  isACHSupported,
  nickname,
  firstName,
  lastName
}) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const navigate = useNavigate();
  const { createCommonErrorToast } = useToastContext();
  const { openModal } = useModalContext();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [updatePayeeStatusLoading, setUpdatePayeeStatusLoading] = useState(false);
  const usedAtDashboard = usedAt && usedAt === 'dashboard';
  const bankingPath = useBankingPath();
  const {
    getPayments,
    loading: loadingPayments,
    data: paymentData,
    error: paymentError
  } = useGetPayment(id);

  const isArchived = COUNTER_PARTY_STATUS.ARCHIVED === status;

  const handleOpen = (event) => {
    event.stopPropagation();
    getPayments();
    setIsOpenModal(true);
    setAnchorEl(event.currentTarget);
    event.currentTarget.blur();
  };

  const clickOnItemHandler = () => {
    if (usedAtDashboard) {
      navigate(generatePath(`${bankingPath}/payees/:id`, { id }));
    }
  };

  const handleClose = useCallback((event) => {
    event?.stopPropagation();
    setIsOpenModal(false);
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (paymentError) {
      handleClose();
      createCommonErrorToast({ error: paymentError });
    }
  }, [paymentError]);

  const handleConfirmDeletePayee = useCallback(
    (event) => {
      handleClose(event);
      openModal({
        component: ConfirmDeletePayeeModal,
        id,
        firstName: name,
        handleSuccess: handleDeleteSuccess
      });
    },
    [openModal, handleClose, handleDeleteSuccess, id, name]
  );

  const trackSendMoneyClick = useButtonPressedCallback({
    elementName: 'sendm_topayee_button',
    elementText: 'Send money',
    appPageName: 'payees_screen'
  });

  const handleClickSendMoneyButton = useCallback(
    (event) => {
      const counterParty = {
        id,
        name,
        bank,
        accountNumber,
        isArchived,
        status,
        connectedType,
        accountType,
        fullAccountNumber,
        wire,
        isACHSupported,
        isWireSupported
      };

      trackSendMoneyClick();
      handleClose(event);
      openModal({
        name: MODAL_NAMES.SEND_MONEY_MODAL,
        counterParty,
        entryPageForSendMoney: `${bankingPath}/payees`
      });
    },
    [
      accountNumber,
      accountType,
      bank,
      businessEntityUuid,
      connectedType,
      fullAccountNumber,
      id,
      isArchived,
      name,
      openModal,
      status,
      wire
    ]
  );

  const hasTransactionHistory = paymentData?.bankingPayments?.bankingPayments?.length !== 0;

  const deleteOrUnarchiveOrArchiveHandler = useCallback(
    async (event) => {
      handleClose(event);
      // If no transaction history, prompt for confirmation to delete payee
      if (!hasTransactionHistory) {
        handleConfirmDeletePayee(event);
        return;
      }

      // If there is transaction history, update the payee status
      const newStatus = isArchived ? COUNTER_PARTY_STATUS.ACTIVE : COUNTER_PARTY_STATUS.ARCHIVED;

      setUpdatePayeeStatusLoading(true);
      await handleUpdatePayeeStatus({ id, name, status: newStatus });
      setUpdatePayeeStatusLoading(false);
    },
    [id, name, isArchived, hasTransactionHistory]
  );

  const handleClickMenuItem = async (event) => {
    if (hasTransactionHistory) {
      handleClose(event);
      setUpdatePayeeStatusLoading(true);
      await handleUpdatePayeeStatus({
        id,
        name,
        status: COUNTER_PARTY_STATUS.ARCHIVED
      });
      setUpdatePayeeStatusLoading(false);
    } else {
      handleConfirmDeletePayee(event);
    }
  };

  const trackEditPayeeClick = useButtonPressedCallback({
    elementName: 'Edit_Payee_clicked',
    elementText: 'Edit',
    appPageName: 'payees_screen'
  });

  const handleEditPayee = useCallback(() => {
    const counterPartyInfo = {
      id,
      name,
      bank,
      connectedType,
      accountType,
      fullAccountNumber,
      wire,
      type,
      routingNumber,
      accountNumber,
      isACHSupported,
      isWireSupported,
      fullRoutingNumber,
      nickname,
      firstName,
      lastName
    };

    trackEditPayeeClick();
    openModal({
      component: AddCounterPartyModal,
      mode: AddCounterPartyModal.MODAL_TYPES.EDIT_PAYEE,
      counterPartyInfo,
      refetch: handleDeleteSuccess
    });
    setTimeout(() => {
      handleClose();
    }, 1000);
  }, []);

  const getMenuItems = useCallback(() => {
    const menuItemWhenUsedAtDashboard = [
      // Send money option
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="send-money-item"
        onClick={(event) => handleClickSendMoneyButton(event)}
        sx={{
          color: `${theme.palette.text.primary}`
        }}
      >
        Send money
      </MenuItemConnectedAccount>,

      // View history option
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="view-history-item"
        onClick={(event) => {
          event.stopPropagation();
          navigate(generatePath(`${bankingPath}/payees/:id`, { id }));
        }}
        sx={{
          color: `${theme.palette.text.primary}`
        }}
      >
        View history
      </MenuItemConnectedAccount>,

      // Archive/Unarchive/Delete option
      <MenuItemConnectedAccount
        aria-label="item-action"
        key="unlink-link-account"
        onClick={(event) => deleteOrUnarchiveOrArchiveHandler(event)}
        sx={{
          color: `${theme.palette.text.red}`
        }}
      >
        {
          // eslint-disable-next-line no-nested-ternary
          !hasTransactionHistory ? 'Delete' : isArchived ? 'Unarchive' : 'Archive'
        }
      </MenuItemConnectedAccount>
    ];

    if (isArchived) {
      // Remove Send money option from menu list
      menuItemWhenUsedAtDashboard.shift();
    }

    const contentArchived = (
      <Stack
        alignItems="center"
        direction="row"
        gap="12px"
        justifyContent="center"
      >
        <Box width="19px">
          <SharedAsset
            alt=""
            color={theme.palette.text.red}
            src="/s2/icon/archive-fill.svg"
            width={38}
          />
        </Box>
        Archive
      </Stack>
    );

    // TODO: Should update the img to SharedAsset component when ZUI is released
    const contentDelete = (
      <Stack
        alignItems="center"
        direction="row"
        gap="12px"
        justifyContent="center"
      >
        <Box width="19px">
          <SharedAsset
            alt=""
            color={theme.palette.text.red}
            src="/s2/icon/trash-can-fill.svg"
            width={38}
          />
        </Box>
        Delete
      </Stack>
    );

    // TODO: Should update the img to SharedAsset component when ZUI is released
    const defaultMenuItems = [
      // Transaction history option
      <MenuItem
        aria-label="action item"
        key="transaction-history"
        onClick={() => navigate(generatePath(`${bankingPath}/payees/:id`, { id }))}
      >
        <Stack
          alignItems="center"
          direction="row"
          gap="12px"
          justifyContent="center"
        >
          <Box width="19px">
            <SharedAsset
              alt=""
              color={theme.palette.text.tertiary}
              src="/s2/material-icons/removeredeyerounded.svg"
              width={38}
            />
          </Box>
          View history
        </Stack>
      </MenuItem>,

      // Edit option
      !hasTransactionHistory && (
        <MenuItem
          aria-label="action item"
          key="edit-payee"
          onClick={handleEditPayee}
        >
          <Stack
            alignItems="center"
            direction="row"
            gap="12px"
            justifyContent="center"
          >
            <Box width="19px">
              <SharedAsset
                alt=""
                color={theme.palette.text.tertiary}
                src="/s2/material-icons/editrounded.svg"
                width={38}
              />
            </Box>
            Edit
          </Stack>
        </MenuItem>
      ),

      // Archive/Delete option (conditionally rendered based on 'isArchived')
      !isArchived && (
        <Stack key="delete-or-archive-payee">
          <Divider />
          <MenuItem
            aria-label="action item"
            onClick={handleClickMenuItem}
            sx={{ color: 'text.red' }}
          >
            {hasTransactionHistory ? contentArchived : contentDelete}
          </MenuItem>
        </Stack>
      )
    ];

    return usedAtDashboard ? menuItemWhenUsedAtDashboard : defaultMenuItems;
  }, [
    id,
    name,
    isArchived,
    paymentData,
    usedAtDashboard,
    businessEntityUuid,
    handleClickMenuItem,
    hasTransactionHistory,
    handleClickSendMoneyButton,
    deleteOrUnarchiveOrArchiveHandler
  ]);

  // TODO: handle send money
  const ButtonUnArchiveOrSendMoney = useMemo(() => {
    // Hide this button on the dashboard
    if (usedAtDashboard) {
      return null;
    }

    const buttonText = isArchived ? 'Unarchive' : 'Send money';

    const handleClick = async (e) => {
      e.target.blur();

      if (isArchived) {
        setUpdatePayeeStatusLoading(true);

        await handleUpdatePayeeStatus({
          id,
          name,
          status: COUNTER_PARTY_STATUS.ACTIVE
        });
        setUpdatePayeeStatusLoading(false);
      } else {
        handleClickSendMoneyButton();
      }
    };

    return (
      <LoadingButton
        aria-label="archive or send money action"
        disabled={loadingAccounts || updatePayeeStatusLoading}
        loading={updatePayeeStatusLoading}
        onClick={handleClick}
        size="lg"
        variant="outlined"
      >
        {buttonText}
      </LoadingButton>
    );
  }, [
    id,
    name,
    isArchived,
    loadingAccounts,
    usedAtDashboard,
    handleUpdatePayeeStatus,
    updatePayeeStatusLoading,
    handleClickSendMoneyButton
  ]);

  return (
    <PayeeWrapper
      aria-label="payee item"
      isLatest={isLatest}
      usedAtDashboard={PayeeWrapper}
    >
      <AccountRow
        accountNumber={accountNumber}
        accountType={usedAt === 'dashboard' ? accountType : null}
        anchorEl={anchorEl}
        bankName={bank}
        button={ButtonUnArchiveOrSendMoney}
        clickOnItemHandler={clickOnItemHandler}
        disabled={loadingPayments || updatePayeeStatusLoading}
        handleClose={handleClose}
        handleOpen={handleOpen}
        id={id}
        isACHSupported={isACHSupported}
        isArchived={isArchived}
        isOpenModal={isOpenModal}
        isWireSupported={isWireSupported}
        loading={loadingPayments || updatePayeeStatusLoading}
        menuItems={getMenuItems()}
        name={name}
        usedAt={usedAt}
      />
    </PayeeWrapper>
  );
}

Payee.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  bank: PropTypes.string.isRequired,
  accountNumber: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  connectedType: PropTypes.string.isRequired,
  accountType: PropTypes.string.isRequired,
  handleUpdatePayeeStatus: PropTypes.func,
  handleDeleteSuccess: PropTypes.func,
  loadingAccounts: PropTypes.bool,
  fullAccountNumber: PropTypes.string,
  isWireSupported: PropTypes.bool,
  isACHSupported: PropTypes.bool,
  type: PropTypes.string,
  routingNumber: PropTypes.string,
  fullRoutingNumber: PropTypes.string,
  nickname: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  wire: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      state: PropTypes.string,
      street: PropTypes.string,
      street2: PropTypes.string
    }),
    routingNumber: PropTypes.string
  }),
  usedAt: PropTypes.string,
  isLatest: PropTypes.bool
};
export default memo(Payee);
